import { formatIndicatorValue } from '@/entities/economic-indicator';
import { t } from '@/i18n';
import { signOf } from '@/shared/lib/utils/price-tone';
import { defineServerEnum } from '@/shared/lib/utils/server-enum';

import type { FinancialCalendarEvent } from '@/entities/financial-calendar/types/financial-calendar';
import type { IndicatorUnitCode } from '@/entities/economic-indicator';

/**
 * TRAD-3029 — 캘린더 발표값(actual)·이전치(previous)를 로케일로 조립한다.
 *
 * 정본: Confluence 282329090 §5-23 v27(actualBasis 라벨·미지값 폴백) · 288194563 §2-11 v12(THOUSAND_PERSONS 표기).
 * 종전: 서버가 만든 영문 완성 문자열(«2.9% YoY» · «-48K MoM» · «21,539.2B USD»)을 그대로 — KO 에서도 영문.
 * 지금: 숫자(actualValue/previousValue)·단위(actualUnit)·기준(actualBasis)으로 조립 — 숫자 표기는 경제지표 카드와 같은 함수
 *       (formatIndicatorValue: PERCENT 소수 둘째 · BILLION_USD compact · THOUSAND_PERSONS 만/억 + « 명»).
 *
 * 🔴 폴백이 먼저다(§5-23 미지값 행 · 7절): 기준·단위를 모르거나 숫자가 없으면 **기존 문자열을 한 글자도 안 바꾸고** 돌려준다
 *    (구버전 서버 = 필드 없음 → 종전 화면 그대로). «알 수 없음»으로 바꾸지 않는다 — 같은 발표값의 다른 표기라서.
 * - 기준 라벨은 actual 에만(YOY_PCT «전년 대비»/«YoY» · MOM_DIFF «전월 대비»/«MoM» · LEVEL 없음). 이전치는 숫자+단위만.
 * - MOM_DIFF 는 증감이라 부호를 붙인다(+/-, 0 은 부호 없음 — TRAD-3670 signOf · 하이픈 U+002D). 이전치도 증감이면 같은 부호.
 * - 추세 화살표(getValueTrend)는 이 함수가 아니라 종전 문자열 비교를 그대로 쓴다(팀장 지시) — 여기서 다루지 않는다.
 */
const ACTUAL_BASIS = defineServerEnum(['LEVEL', 'YOY_PCT', 'MOM_DIFF'] as const);
const ACTUAL_UNIT = defineServerEnum([
  'PERCENT',
  'INDEX',
  'BILLION_USD',
  'BILLION_KRW',
  'USD',
  'THOUSAND_PERSONS',
  'KRW_PER_USD',
] as const satisfies readonly IndicatorUnitCode[]);


type CalendarValueFields = Pick<
  FinancialCalendarEvent,
  'actual' | 'previous' | 'actualValue' | 'previousValue' | 'actualBasis' | 'actualUnit'
>;

/** 부호 판정 = 화면에 보이는 반올림. THOUSAND_PERSONS 는 사람 단위(×1,000) 정수, 그 밖은 소수 둘째(TRAD-3670). */
const SIGN_SCALE: Partial<Record<IndicatorUnitCode, number>> = { THOUSAND_PERSONS: 1_000 };
const signOfShown = (unit: IndicatorUnitCode, value: number) => {
  const scale = SIGN_SCALE[unit];
  return scale === undefined ? signOf(value, 2) : signOf(value * scale, 0);
};

export const formatCalendarValue = (event: CalendarValueFields, which: 'actual' | 'previous'): string | null => {
  const legacy = which === 'actual' ? event.actual : event.previous;
  const value = which === 'actual' ? event.actualValue : event.previousValue;
  const basis = event.actualBasis;
  const unit = event.actualUnit;
  if (!ACTUAL_BASIS.isKnown(basis) || !ACTUAL_UNIT.isKnown(unit)) return legacy;
  if (typeof value !== 'number' || !Number.isFinite(value)) return legacy;

  // 서버 enum 분기는 match(TRAD-3690 래칫) — 위 가드로 UNKNOWN 은 오지 않지만 표에는 둔다.
  const signed = ACTUAL_BASIS.match(basis, { MOM_DIFF: true, YOY_PCT: false, LEVEL: false, UNKNOWN: false });
  const number = signed
    ? `${signOfShown(unit, value)}${formatIndicatorValue(unit, Math.abs(value))}`
    : formatIndicatorValue(unit, value);
  if (which === 'previous') return number;
  const label = ACTUAL_BASIS.match(basis, {
    YOY_PCT: t('financial_calendar.actual_basis.yoy_pct'),
    MOM_DIFF: t('financial_calendar.actual_basis.mom_diff'),
    LEVEL: null,
    UNKNOWN: null,
  });
  return label === null ? number : `${number} ${label}`;
};
